/**
 * Installation de Boutique Pro comme une application (téléphone, tablette, ordinateur).
 *
 * Le navigateur annonce qu'on peut installer l'application (événement
 * « beforeinstallprompt », Chrome / Edge / Android) une seule fois, souvent
 * avant l'affichage des écrans : on le garde ici dès le démarrage, et le bouton
 * « Installer l'application » s'en sert quand on clique.
 * Sur iPhone / iPad (Safari), il n'y a pas d'installation automatique : on
 * affiche la marche à suivre (Partager → Sur l'écran d'accueil).
 */
import { reactive } from "vue";

const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";

function modeApplication() {
  if (typeof window === "undefined") return false;
  return (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true;
}

export const installation = reactive({
  /** Proposition d'installation du navigateur, gardée pour le clic */
  proposition: null,
  /** Déjà ouverte comme application installée */
  installee: modeApplication(),
  /** iPhone / iPad : installation à la main depuis Safari */
  ios: /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1),
  /** Le bouton a un sens : proposition disponible, ou iPhone/iPad pas encore installé */
  get possible() {
    return !this.installee && (!!this.proposition || this.ios);
  },
});

export function surveillerInstallation() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // on choisit nous-mêmes le moment (bouton)
    installation.proposition = e;
  });
  window.addEventListener("appinstalled", () => {
    installation.proposition = null;
    installation.installee = true;
  });
  if (window.matchMedia) {
    const m = window.matchMedia("(display-mode: standalone)");
    const maj = () => { installation.installee = modeApplication(); };
    if (m.addEventListener) m.addEventListener("change", maj);
    else if (m.addListener) m.addListener(maj);
  }
}

/**
 * Lance l'installation.
 * @returns {Promise<"installee"|"refusee"|"ios"|"indisponible">}
 */
export async function installer() {
  if (installation.proposition) {
    const p = installation.proposition;
    installation.proposition = null; // une proposition ne sert qu'une fois
    await p.prompt();
    const choix = await p.userChoice.catch(() => ({ outcome: "dismissed" }));
    return choix.outcome === "accepted" ? "installee" : "refusee";
  }
  if (installation.ios) return "ios";
  return "indisponible";
}
